/**
 * Ethiopia's monthly HMIS report, computed from the records ASSIST holds.
 *
 * - Periods follow the national HMIS rule: a monthly report covers the 21st of the previous
 *   Ethiopian month to the 20th of the reporting month, so Pagume falls into Meskerem's report.
 *   The period id is the Ethiopian year and month, YYYYMM, as Ethiopia's DHIS2 (Ethiopian
 *   calendar) expects.
 * - What is counted is declared by the content pack (`hmis` in the pack), so the code knows two
 *   kinds of element, not any disease:
 *   - `enrolled`: patients in care in the period, by age band, sex, care type and timing;
 *   - `cohort_outcome`: patients enrolled N months earlier, by their outcome at the period end.
 *
 * Interpretations of the HMIS guide that the guide leaves open are written down where they are
 * made (look for "INTERPRETATION") and in docs/decisions.md, for the HMIS team to confirm.
 */
import { conceptOf, daysBetween, drugOf, observationValue } from './context.js';
import { ethToIso, isoToEth } from './ethiopian.js';
import { parse, test } from './expr.js';
import { EXT_EPISODE_END_REASON, SYS_PROGRAMME } from './fhir.js';
import { isDeceased, programmeVisits } from './followup.js';
import { computeMeasure } from './report.js';
import { addDays, nextVisitOf } from './visit.js';
const pad2 = (n) => String(n).padStart(2, '0');
/** The HMIS reporting period for an Ethiopian month (1–12), with the national 21st–20th rule. */
export function hmisPeriod(year, month, cutoffDay = 20) {
    if (!Number.isInteger(month) || month < 1 || month > 12)
        throw new RangeError('HMIS months are 1 (Meskerem) to 12 (Nehase); Pagume belongs to Meskerem');
    const prev = month === 1
        ? { year: year - 1, month: 12, day: cutoffDay + 1 }
        : { year, month: month - 1, day: cutoffDay + 1 };
    const start = ethToIso(prev);
    const end = ethToIso({ year, month, day: cutoffDay });
    if (!start || !end)
        throw new RangeError('Date outside the supported range');
    return { id: `${year}${pad2(month)}`, year, month, start, end };
}
export function parseHmisPeriodId(id, cutoffDay = 20) {
    const m = /^(\d{4})(\d{2})$/.exec(id);
    if (!m)
        throw new RangeError('Period must be YYYYMM (Ethiopian calendar)');
    return hmisPeriod(Number(m[1]), Number(m[2]), cutoffDay);
}
/** The period `n` months before `p`. */
export function shiftPeriod(p, n, cutoffDay = 20) {
    const idx = p.year * 12 + (p.month - 1) - n;
    return hmisPeriod(Math.floor(idx / 12), (idx % 12) + 1, cutoffDay);
}
/** The latest period that has fully ended on `todayIso`. */
export function lastCompletePeriod(todayIso, cutoffDay = 20) {
    const e = isoToEth(todayIso);
    if (!e)
        throw new RangeError('Date outside the supported range');
    // On or before the cutoff, this month's report is still open.
    let p = e.month === 13 ? hmisPeriod(e.year + 1, 1, cutoffDay) : hmisPeriod(e.year, e.month, cutoffDay);
    while (p.end >= todayIso)
        p = shiftPeriod(p, 1, cutoffDay);
    return p;
}
const bandLabel = ([a, b]) => (b === null ? `${a}+` : `${a}-${b}`);
function ageOn(p, iso) {
    const b = p.birthDate;
    if (!b)
        return null;
    const y = Number(iso.slice(0, 4)) - Number(b.slice(0, 4));
    if (b.length < 10)
        return y;
    return iso.slice(5, 10) < b.slice(5, 10) ? y - 1 : y;
}
const programmeEpisodes = (b, programme) => b.record.episodes.filter((e) => e.status !== 'entered-in-error' &&
    e.type.some((t) => t.coding?.some((c) => c.system === SYS_PROGRAMME && c.code === programme)));
const endReason = (e) => (e.extension ?? []).find((x) => x.url === EXT_EPISODE_END_REASON)?.valueCode ?? null;
const startOf = (e) => (e.period?.start ?? '').slice(0, 10);
const endOf = (e) => (e.period?.end ?? '').slice(0, 10);
function tally(def, people, period) {
    const counts = new Map();
    let outside = 0;
    for (const { b, dims } of people) {
        const age = ageOn(b.patient, period.end);
        const band = age === null
            ? null
            : def.age_bands.find(([lo, hi]) => age >= lo && (hi === null || age <= hi));
        if (!band)
            outside++;
        const all = {
            ...dims,
            sex: b.patient.gender,
            age: band ? bandLabel(band) : 'unknown',
        };
        for (const g of def.groups) {
            const key = g.map((d) => `${d}=${all[d] ?? 'unknown'}`).join(',');
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
    }
    // Every expected combination appears, with 0 when nobody is in it (DHIS2 needs explicit zeros).
    const expected = (g) => {
        let keys = [[]];
        for (const d of g) {
            const opts = d === 'age'
                ? def.age_bands.map(bandLabel)
                : d === 'sex'
                    ? ['male', 'female']
                    : d === 'care'
                        ? ['hlc', 'pharm']
                        : d === 'timing'
                            ? ['new', 'previous']
                            : d === 'outcome'
                                ? [
                                    'controlled',
                                    'uncontrolled',
                                    'ltfu',
                                    'died',
                                    'transferred_out',
                                    'not_evaluated',
                                ]
                                : [];
            keys = keys.flatMap((k) => opts.map((o) => [...k, `${d}=${o}`]));
        }
        return keys.map((k) => k.join(','));
    };
    // Report order: the groups as declared, each in the HMIS form's order; anything unexpected last.
    const order = def.groups.flatMap(expected);
    for (const k of order)
        if (!counts.has(k))
            counts.set(k, 0);
    const rank = (k) => {
        const i = order.indexOf(k);
        return i < 0 ? order.length : i;
    };
    const values = [
        {
            element: def.code,
            key: def.kind === 'cohort_outcome' ? 'registered' : 'total',
            value: people.length,
        },
        ...[...counts.entries()]
            .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
            .map(([key, value]) => ({ element: def.code, key, value })),
    ];
    return {
        code: def.code,
        title: def.title,
        total: people.length,
        values,
        outsideAgeBands: outside,
    };
}
/**
 * enrolled: in care in the period.
 * INTERPRETATION (as Simple's export to Ethiopian DHIS2 does): "newly enrolled" = enrolment
 * started in the period; "previously in care" = enrolled before the period, still enrolled, alive,
 * and with a programme visit in the 12 months up to the period end.
 */
function enrolled(def, bundles, period) {
    const care = new Set(def.care_drugs ?? []);
    const people = [];
    for (const b of bundles) {
        const eps = programmeEpisodes(b, def.programme).filter((e) => startOf(e) && startOf(e) <= period.end && (e.status === 'active' || endOf(e) > period.end));
        const ep = eps[0];
        if (!ep)
            continue;
        // Deceased with an enrolment never closed: no longer in care.
        if (isDeceased(b.patient) && ep.status === 'active')
            continue;
        const isNew = startOf(ep) >= period.start;
        if (!isNew) {
            const seen = programmeVisits(b.record, def.programme).some((v) => {
                const d = v.period.start.slice(0, 10);
                return d <= period.end && daysBetween(d, period.end) < 365;
            });
            if (!seen)
                continue;
        }
        const onDrug = b.record.medications.some((m) => care.has(drugOf(m) ?? '') &&
            m.authoredOn.slice(0, 10) <= period.end &&
            (m.status === 'active' || m.status === 'completed'));
        people.push({
            b,
            dims: { timing: isNew ? 'new' : 'previous', care: onDrug ? 'pharm' : 'hlc' },
        });
    }
    return tally(def, people, period);
}
/**
 * cohort_outcome: patients whose enrolment started in the period `cohort_months_ago` before,
 * with their outcome at the end of this period, in this order of precedence:
 * died > transferred out > lost to follow-up > controlled / uncontrolled > not evaluated.
 * INTERPRETATION: "controlled at the last clinical visit" = the values recorded at the last
 * programme visit up to the period end; lost to follow-up = more than N days (28) past the
 * appointment set at that visit (or the default 28-day return) by the period end.
 */
function cohort(def, bundles, period, cutoffDay) {
    const reg = shiftPeriod(period, def.cohort_months_ago ?? 6, cutoffDay);
    const ast = parse(def.condition ?? 'false');
    const ltfuDays = def.ltfu_days_after_appointment ?? 28;
    const people = [];
    for (const b of bundles) {
        const ep = programmeEpisodes(b, def.programme).find((e) => startOf(e) >= reg.start && startOf(e) <= reg.end);
        if (!ep)
            continue;
        let outcome;
        const ended = endOf(ep) && endOf(ep) <= period.end ? endReason(ep) : null;
        if (isDeceased(b.patient) || ended === 'died')
            outcome = 'died';
        else if (ended === 'transferred-out')
            outcome = 'transferred_out';
        else {
            const visits = programmeVisits(b.record, def.programme).filter((v) => v.period.start.slice(0, 10) <= period.end);
            const last = visits[0];
            const lastDate = last ? last.period.start.slice(0, 10) : startOf(ep);
            const appointment = (last && nextVisitOf(last)) ?? addDays(lastDate, 28);
            if (daysBetween(appointment, period.end) > ltfuDays)
                outcome = 'ltfu';
            else if (!last)
                outcome = 'not_evaluated';
            else {
                const ctx = {};
                let any = false;
                for (const c of def.concepts ?? []) {
                    const o = b.record.observations.find((x) => x.status !== 'entered-in-error' &&
                        conceptOf(x) === c &&
                        x.encounter?.reference === `Encounter/${last.id}`);
                    ctx[c] = o ? observationValue(o) : null;
                    if (o)
                        any = true;
                }
                outcome = !any ? 'not_evaluated' : test(ast, ctx) ? 'controlled' : 'uncontrolled';
            }
        }
        people.push({ b, dims: { outcome } });
    }
    return tally(def, people, period);
}
export function computeHmisReport(def, bundles, period, measures = []) {
    const cache = new Map();
    return {
        period,
        elements: def.elements.map((e) => {
            if (e.kind === 'enrolled')
                return enrolled(e, bundles, period);
            if (e.kind === 'cohort_outcome')
                return cohort(e, bundles, period, def.cutoff_day);
            const m = measures.find((x) => x.id === e.measure);
            const r = m
                ? computeMeasure(m, bundles, period.start, period.end, cache)
                : { id: '', count: 0, groups: undefined };
            return {
                code: e.code,
                title: e.title,
                total: r.count,
                values: [
                    { element: e.code, key: 'total', value: r.count },
                    ...(r.groups ?? []).map(([k, n]) => ({ element: e.code, key: `value=${k}`, value: n })),
                ],
                outsideAgeBands: 0,
            };
        }),
    };
}
//# sourceMappingURL=hmis.js.map